import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "container mx-auto px-4 py-8" }
const _hoisted_2 = { class: "mb-8" }
const _hoisted_3 = { class: "border-b border-gray-700" }
const _hoisted_4 = { class: "-mb-px flex space-x-8" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 0,
  class: "mb-8"
}
const _hoisted_7 = {
  key: 1,
  class: "mb-8"
}
const _hoisted_8 = {
  key: 2,
  class: "space-y-8"
}
const _hoisted_9 = {
  key: 0,
  class: "flex justify-center items-center py-12"
}
const _hoisted_10 = {
  key: 1,
  class: "bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6"
}
const _hoisted_11 = { class: "text-red-400" }
const _hoisted_12 = {
  key: 2,
  class: "space-y-6"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "flex items-center space-x-4 flex-1" }
const _hoisted_15 = ["onUpdate:modelValue"]
const _hoisted_16 = { class: "flex items-center gap-4" }
const _hoisted_17 = { class: "relative" }
const _hoisted_18 = ["onUpdate:modelValue"]
const _hoisted_19 = { class: "ml-2 text-sm text-gray-400" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "border-t border-gray-700" }
const _hoisted_22 = { class: "p-4 space-y-4" }
const _hoisted_23 = { class: "space-y-2" }
const _hoisted_24 = { class: "relative" }
const _hoisted_25 = { class: "flex justify-end items-center gap-3" }
const _hoisted_26 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_27 = ["onClick", "disabled"]
const _hoisted_28 = {
  key: 0,
  class: "inline-block animate-spin"
}

import { ref, reactive, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { db } from '@/config/firebase'
import SchemaBuilder, { type SchemaField } from '@/components/content/SchemaBuilder.vue'
import CodeEditor from '@/components/content/CodeEditor.vue'
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  setDoc
} from 'firebase/firestore'

interface Callback {
  id?: string
  title: string
  isActive: boolean
  code: string
  lastSaved?: number
  created_at?: Timestamp
  updated_at?: Timestamp
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PersonaliseView',
  setup(__props) {

const authStore = useAuthStore()
const isLoading = ref<boolean>(true)
const isSaving = ref<boolean>(false)
const isSavingSchema = ref<boolean>(false)
const savingIndex = ref<number | null>(null)
const error = ref<string | null>(null)
const callbacks = ref<Callback[]>([])
const contextSchema = ref<SchemaField[]>([])
const integrationSchema = ref<SchemaField[]>([])
const isSavingIntegrationSchema = ref<boolean>(false)

// Tabs
const currentTab = ref('callbacks')
const tabs = [
  { id: 'callbacks', name: 'Callbacks' },
  { id: 'schema', name: 'Personalised Data' },
  { id: 'integration', name: 'Integration Data' }
]

const expandedCallbacks = reactive<{ [key: number]: boolean }>({})

const loadCallbacks = async () => {
  if (!authStore.user) {
    error.value = 'No user logged in'
    return
  }

  isLoading.value = true
  error.value = null

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Query callbacks for this org
    const callbacksQuery = query(
      collection(db, 'callbacks'),
      where('org_id', '==', org_id)
    )

    const querySnapshot = await getDocs(callbacksQuery)
    
    callbacks.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title,
      code: doc.data().callback,
      isActive: doc.data().is_active,
      created_at: doc.data().created_at,
      updated_at: doc.data().updated_at,
      lastSaved: doc.data().updated_at?.toMillis()
    }))

    // If no callbacks exist, create an empty one
    if (callbacks.value.length === 0) {
      callbacks.value.push({
        title: '',
        isActive: true,
        code: ''
      })
    }

    // Initialize expanded state
    callbacks.value.forEach((_, index) => {
      expandedCallbacks[index] = index === 0
    })

  } catch (err: any) {
    console.error('Error loading callbacks:', err)
    error.value = err.message
  } finally {
    isLoading.value = false
  }
}

const toggleCallback = (index: number) => {
  expandedCallbacks[index] = !expandedCallbacks[index]
}

const addCallback = () => {
  const newIndex = callbacks.value.length
  callbacks.value.push({
    title: '',
    isActive: true,
    code: ''
  })
  expandedCallbacks[newIndex] = true
}

const removeCallback = async (index: number) => {
  const callback = callbacks.value[index]
  
  if (callback.id) {
    try {
      await deleteDoc(doc(db, 'callbacks', callback.id))
    } catch (err) {
      console.error('Error deleting callback:', err)
      return
    }
  }

  callbacks.value.splice(index, 1)
  delete expandedCallbacks[index]
  
  // Reindex remaining expanded states
  for (let i = index; i < callbacks.value.length; i++) {
    expandedCallbacks[i] = expandedCallbacks[i + 1]
    delete expandedCallbacks[i + 1]
  }

  // If no callbacks left, add an empty one
  if (callbacks.value.length === 0) {
    addCallback()
  }
}

const saveCallback = async (index: number) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  const callback = callbacks.value[index]
  if (!callback.title.trim() || !callback.code.trim()) {
    console.error('Title and code are required')
    return
  }

  isSaving.value = true
  savingIndex.value = index

  try {
    // Get user's document to get org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Prepare callback data
    const callbackData = {
      org_id,
      title: callback.title.trim(),
      callback: callback.code.trim(),
      is_active: callback.isActive,
      updated_at: serverTimestamp()
    }

    let docRef;
    
    if (callback.id) {
      // Update existing document
      docRef = doc(db, 'callbacks', callback.id)
      await updateDoc(docRef, callbackData)
    } else {
      // Create new document
      docRef = await addDoc(collection(db, 'callbacks'), {
        ...callbackData,
        created_at: serverTimestamp()
      })
      // Store the document ID
      callbacks.value[index].id = docRef.id
    }

    // Update last saved timestamp
    callbacks.value[index].lastSaved = Date.now()
    
    console.log('Callback saved successfully')
  } catch (error) {
    console.error('Error saving callback:', error)
  } finally {
    isSaving.value = false
    savingIndex.value = null
  }
}

const saveSchema = async (schema: any[]) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  isSavingSchema.value = true
  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Save schema to org config
    await setDoc(doc(db, 'config', org_id), {
      context_schema: schema
    }, { merge: true })

    contextSchema.value = schema
  } catch (error: any) {
    console.error('Error saving schema:', error)
  } finally {
    isSavingSchema.value = false
  }
}

const loadContextSchema = async () => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Load schema from org config
    const configDoc = await getDoc(doc(db, 'config', org_id))
    if (configDoc.exists()) {
      contextSchema.value = configDoc.data().context_schema || []
    }
  } catch (error: any) {
    console.error('Error loading schema:', error)
  }
}

const saveIntegrationSchema = async (schema: any[]) => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  isSavingIntegrationSchema.value = true
  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Save schema to org config
    await setDoc(doc(db, 'config', org_id), {
      integration_schema: schema
    }, { merge: true })

    integrationSchema.value = schema
  } catch (error: any) {
    console.error('Error saving integration schema:', error)
  } finally {
    isSavingIntegrationSchema.value = false
  }
}

const loadIntegrationSchema = async () => {
  if (!authStore.user) {
    console.error('No user logged in')
    return
  }

  try {
    // Get user's org_id
    const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
    const userData = userDoc.data()
    const org_id = userData?.org_id

    if (!org_id) {
      throw new Error('No organization ID found')
    }

    // Load schema from org config
    const configDoc = await getDoc(doc(db, 'config', org_id))
    if (configDoc.exists()) {
      integrationSchema.value = configDoc.data().integration_schema || []
    }
  } catch (error: any) {
    console.error('Error loading integration schema:', error)
  }
}

// Load data when component mounts
onMounted(() => {
  loadCallbacks()
  loadContextSchema()
  loadIntegrationSchema()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "mb-8" }, [
      _createElementVNode("h1", { class: "text-2xl font-bold text-gray-100 mb-2" }, "1:1 Personalisation"),
      _createElementVNode("p", { class: "text-gray-400" }, "Create and manage 1:1 personalisation for your customers.")
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("nav", _hoisted_4, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tabs, (tab) => {
            return _createElementVNode("button", {
              key: tab.id,
              onClick: ($event: any) => (currentTab.value = tab.id),
              class: _normalizeClass([
              currentTab.value === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ])
            }, _toDisplayString(tab.name), 11, _hoisted_5)
          }), 64))
        ])
      ])
    ]),
    (currentTab.value === 'schema')
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(SchemaBuilder, {
            modelValue: contextSchema.value,
            onSave: saveSchema,
            isSaving: isSavingSchema.value,
            title: "Personalised Data Schema",
            subtitle: "Define the structure of your personalised data for callbacks"
          }, null, 8, ["modelValue", "isSaving"])
        ]))
      : (currentTab.value === 'integration')
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(SchemaBuilder, {
              modelValue: integrationSchema.value,
              onSave: saveIntegrationSchema,
              isSaving: isSavingIntegrationSchema.value,
              title: "Integration Data Schema",
              subtitle: "Define the structure of your integration data for external systems"
            }, null, 8, ["modelValue", "isSaving"])
          ]))
        : (currentTab.value === 'callbacks')
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              (isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, _cache[2] || (_cache[2] = [
                    _createElementVNode("div", { class: "animate-spin rounded-full h-8 w-8 border-b-2 border-primary" }, null, -1)
                  ])))
                : (error.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createElementVNode("p", _hoisted_11, _toDisplayString(error.value), 1),
                      _createElementVNode("button", {
                        onClick: loadCallbacks,
                        class: "mt-2 text-sm text-red-400 hover:text-red-300"
                      }, " Try Again ")
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(callbacks.value, (callback, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: callback.id || index,
                          class: "bg-dark-secondary rounded-lg border border-gray-700 hover:border-primary/30 transition-all duration-300"
                        }, [
                          _createElementVNode("div", {
                            class: "p-4 flex items-center justify-between cursor-pointer",
                            onClick: ($event: any) => (toggleCallback(index))
                          }, [
                            _createElementVNode("div", _hoisted_14, [
                              _createElementVNode("div", {
                                class: _normalizeClass(["transform transition-transform duration-200", expandedCallbacks[index] ? 'rotate-90' : ''])
                              }, _cache[3] || (_cache[3] = [
                                _createElementVNode("svg", {
                                  xmlns: "http://www.w3.org/2000/svg",
                                  class: "h-5 w-5 text-gray-400",
                                  viewBox: "0 0 20 20",
                                  fill: "currentColor"
                                }, [
                                  _createElementVNode("path", {
                                    "fill-rule": "evenodd",
                                    d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z",
                                    "clip-rule": "evenodd"
                                  })
                                ], -1)
                              ]), 2),
                              _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": ($event: any) => ((callback.title) = $event),
                                type: "text",
                                placeholder: "Enter callback name...",
                                class: "flex-1 px-3 py-1.5 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                              }, null, 8, _hoisted_15), [
                                [_vModelText, callback.title]
                              ]),
                              _createElementVNode("div", _hoisted_16, [
                                _createElementVNode("label", {
                                  class: "flex items-center cursor-pointer",
                                  onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                                }, [
                                  _createElementVNode("div", _hoisted_17, [
                                    _withDirectives(_createElementVNode("input", {
                                      type: "checkbox",
                                      "onUpdate:modelValue": ($event: any) => ((callback.isActive) = $event),
                                      class: "sr-only"
                                    }, null, 8, _hoisted_18), [
                                      [_vModelCheckbox, callback.isActive]
                                    ]),
                                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "w-10 h-5 bg-gray-700 rounded-full shadow-inner" }, null, -1)),
                                    _createElementVNode("div", {
                                      class: _normalizeClass(["dot absolute w-4 h-4 bg-white rounded-full transition transform", callback.isActive ? 'translate-x-5 bg-primary' : 'translate-x-1'])
                                    }, null, 2)
                                  ]),
                                  _createElementVNode("span", _hoisted_19, _toDisplayString(callback.isActive ? 'Active' : 'Inactive'), 1)
                                ]),
                                _createElementVNode("button", {
                                  onClick: _withModifiers(($event: any) => (removeCallback(index)), ["stop"]),
                                  class: "p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                }, _cache[5] || (_cache[5] = [
                                  _createElementVNode("svg", {
                                    xmlns: "http://www.w3.org/2000/svg",
                                    class: "h-5 w-5",
                                    viewBox: "0 0 20 20",
                                    fill: "currentColor"
                                  }, [
                                    _createElementVNode("path", {
                                      "fill-rule": "evenodd",
                                      d: "M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z",
                                      "clip-rule": "evenodd"
                                    })
                                  ], -1)
                                ]), 8, _hoisted_20)
                              ])
                            ])
                          ], 8, _hoisted_13),
                          _withDirectives(_createElementVNode("div", _hoisted_21, [
                            _createElementVNode("div", _hoisted_22, [
                              _createElementVNode("div", _hoisted_23, [
                                _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-400" }, "JavaScript Code", -1)),
                                _createElementVNode("div", _hoisted_24, [
                                  _createVNode(CodeEditor, {
                                    modelValue: callback.code,
                                    "onUpdate:modelValue": ($event: any) => ((callback.code) = $event),
                                    schema: contextSchema.value
                                  }, null, 8, ["modelValue", "onUpdate:modelValue", "schema"])
                                ])
                              ]),
                              _createElementVNode("div", _hoisted_25, [
                                (callback.lastSaved)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_26, " Last saved: " + _toDisplayString(new Date(callback.lastSaved).toLocaleString()), 1))
                                  : _createCommentVNode("", true),
                                _createElementVNode("button", {
                                  onClick: ($event: any) => (saveCallback(index)),
                                  class: "px-4 py-2 bg-primary text-white hover:bg-primary/90 rounded-lg text-sm font-medium transition-colors flex items-center gap-2",
                                  disabled: isSaving.value
                                }, [
                                  (isSaving.value && savingIndex.value === index)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_28, _cache[7] || (_cache[7] = [
                                        _createElementVNode("svg", {
                                          class: "w-4 h-4",
                                          xmlns: "http://www.w3.org/2000/svg",
                                          fill: "none",
                                          viewBox: "0 0 24 24"
                                        }, [
                                          _createElementVNode("circle", {
                                            class: "opacity-25",
                                            cx: "12",
                                            cy: "12",
                                            r: "10",
                                            stroke: "currentColor",
                                            "stroke-width": "4"
                                          }),
                                          _createElementVNode("path", {
                                            class: "opacity-75",
                                            fill: "currentColor",
                                            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          })
                                        ], -1)
                                      ])))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("span", null, _toDisplayString(isSaving.value && savingIndex.value === index ? 'Saving...' : 'Save Callback'), 1)
                                ], 8, _hoisted_27)
                              ])
                            ])
                          ], 512), [
                            [_vShow, expandedCallbacks[index]]
                          ])
                        ]))
                      }), 128)),
                      _createElementVNode("button", {
                        onClick: addCallback,
                        class: "w-full px-4 py-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-primary hover:border-primary transition-colors text-sm"
                      }, " Add New Callback ")
                    ]))
            ]))
          : _createCommentVNode("", true)
  ]))
}
}

})